import PropTypes from 'prop-types'
import { useState, useLayoutEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import isUndefined from 'lodash/isUndefined'

import colors from 'components/utils/colors'
import { getJsonLinesWithTokens } from './jsonTokenizer'

const INDENTATION_WIDTH = 8
const JSON_TOKEN_COLOR_MAP = {
  'key': colors.secondary20,
  'symbols': colors.secondary70,
  'number': colors.alert65,
  'string': '#775095',
  'boolean': colors.alert65,
  'null': colors.alert65,
}

const StyledWrapper = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.secondary20};

  ${props => !props.noWrap && css`
    word-break: break-word;
  `}

  ${props => props.noWrap && css`
    white-space: nowrap;
  `}

  ${props => props.maxLines && css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${props => props.maxLines};
  `}
`

const JsonLine = styled.div`
  padding-left: ${props => props.indentation * INDENTATION_WIDTH}px;
`

const JsonToken = styled.span`
  color: ${props => props.color};
`

const MoreLink = styled.a`
  display: flex;
  justify-content: end;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  position: relative;
  top: 6px;
  right: 0;
`

const getTokenColor = (type) => {
  return JSON_TOKEN_COLOR_MAP[type]
}

const JsonViewer = ({ json, refreshTime, onMoreClick, maxLines, noWrap }) => {
  const ref = useRef(null)
  const [isCropped, setIsCropped] = useState(false)
  const { t } = useTranslation()

  const refresh = () => {
    if (ref.current) {
      setIsCropped(ref.current.scrollHeight > ref.current.clientHeight)
    }
  }

  useLayoutEffect(() => {
    if (!maxLines) {
      return
    }
    // refresh immediately when possible for quick response
    refresh()
    // when sidebar is expanded we need to update "More" button visibility state
    // and use scrollHeight / clientHeight for that but they are only updated after delay
    if (refreshTime) {
      setTimeout(() => {
        refresh()
      }, refreshTime)
    }
  })

  let jsonObject
  let jsonLinesWithTokens = []

  try {
    jsonObject = JSON.parse(json)
  } catch (e) {
  }

  const jsonObjectExists = !isUndefined(jsonObject)

  if (jsonObjectExists) {
    jsonLinesWithTokens = getJsonLinesWithTokens(jsonObject)
    // keep only first maxLines + 1 lines to reduce number of DOM elements
    if (maxLines) {
      jsonLinesWithTokens = jsonLinesWithTokens.slice(0, maxLines + 1)
    }
  }

  return (
    <>
      <StyledWrapper
        ref={ref}
        maxLines={maxLines}
        noWrap={jsonObjectExists ? noWrap : false}
        data-test='json-viewer'
      >
        {jsonLinesWithTokens.map((line, lineIdx) =>
          <JsonLine
            key={lineIdx}
            indentation={line.indentation}
          >
            {line.tokens.map((token, tokenIdx) =>
              <JsonToken
                key={tokenIdx}
                color={getTokenColor(token.type)}
              >
                {token.value}
              </JsonToken>
            )}
          </JsonLine>
        )}
        {!jsonObjectExists && json}
      </StyledWrapper>

      {maxLines && isCropped && (
        <MoreLink onClick={onMoreClick}>{t('common.moreLink')}</MoreLink>
      )}
    </>
  )
}

JsonViewer.propTypes = {
  json: PropTypes.string,
  refreshTime: PropTypes.number,
  onMoreClick: PropTypes.func,
  maxLines: PropTypes.number,
  noWrap: PropTypes.bool,
}

export default JsonViewer
